import { ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { Router } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialog,
  MatDialogModule,
} from "@angular/material/dialog";
import { AlertDialogComponent } from "../../../shared/alert-dialog/alert-dialog.component";
import { AuthenticationService } from "../../../core/services/authentication.service";
import { NgIf } from "@angular/common";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { TranslateModule } from "@ngx-translate/core";
import { EncryptionDescriptionService } from '../../../core/services/encryption-decryption.service';
import { ApplicationDataService } from "../../../core/services/application-data.service";

@Component({
  selector: "app-otp-verification",
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    NgIf,
  ],
  templateUrl: "./otp-verification.component.html",
  styleUrls: ["./otp-verification.component.css"],
})
export class OtpVerificationComponent implements OnInit {
  form!: FormGroup;
  retryActive: boolean = false;
  resendTimer: string = "02:00";
  timerInterval: any;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<OtpVerificationComponent>,
    public dialog: MatDialog,
    private appDataService: ApplicationDataService,
    private formBuilder: FormBuilder,
    private service: AuthenticationService,
    private cdr: ChangeDetectorRef,
    private cryptService: EncryptionDescriptionService,
    private router: Router
  ) {}

  ngOnInit(): void {
    console.log("Injected Data:", this.data);
  
    this.form = this.formBuilder.group({
      userName: [this.data?.userName || "", [Validators.required]],
      otp: ["", [Validators.required, Validators.minLength(6), Validators.maxLength(6)]],
      newPassword: [this.data?.newPassword || "", [
        Validators.required,
        Validators.minLength(8),
        Validators.maxLength(30),
        Validators.pattern(
          '^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&])[A-Za-z\\d$@$!%*?&]{8,}$'
        ),
      ]],
    });
  
    // Log initial form value
    console.log("Form Initial Value:", this.form.value);
  
    this.startTimer(2);
  }
  

  // Access Form Controls
  get f(): { [key: string]: AbstractControl } {
    return this.form.controls;
  }

  // Save Password
  savePassword() { 
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
  
    const encryptedPassword = this.cryptService.encryptWithPublicKey(this.f['newPassword'].value);
  
    const data = {
      userName: this.f['userName'].value,
      otp: this.f['otp'].value,
      newPassword: encryptedPassword, 
    };
  
    console.log("Payload sent to changePassword:", data);
  
    this.service.changePassword(data).subscribe({
      next: (response) => {
        console.log("Password updated successfully:", response);
        
        // Call logout after successful password change
        this.service.logout().subscribe({
          next: () => {
            console.log('Logged out successfully.');
            this.dialogRef.close({ passwordUpdated: true });
            this.router.navigate(['/login']); // Redirect to /login
          },
          error: (error) => {
            console.error('Error during logout:', error);
            this.openAlert('Failed to logout after password update.');
            this.dialogRef.close({ passwordUpdated: true });
            this.router.navigate(['/login']); // Redirect to /login even if logout fails
          }
        });
      },
      error: (error) => {
        console.error("Error updating password:", error);
  
        // Extract the error message from the response
        let errorMessage = 'Failed to complete operation. Please try again.';
        if (error?.error?.message) {
          // Handle specific error messages from the server
          if (error.error.message.includes('OTP is Expired')) {
            errorMessage = 'OTP is expired. Please try again.';
          } else if (error.error.message.includes('Failed to complete Operation')) {
            errorMessage = 'Please enter the correct OTP.';
          } else {
            errorMessage = error.error.message;
          }
        }
  
        this.openAlert(errorMessage);
      },
    });
  }
  
  retry() {
    // Reset the OTP field
    this.f['otp'].setValue(null);
  
    // Disable retry and restart the timer
    this.retryActive = false;
    this.startTimer(2);
  
    // Construct the payload required for resending the OTP
    const data = {
      userName: this.f['userName'].value,
    };
  
  
    this.service.retryOtp(data).subscribe({
      next: () => {
        this.openAlert('New OTP sent successfully');
      },
      error: (e: any) => {
        console.error('Error during resending OTP:', e);
        if (e?.status === 409 || e?.status === 403) {
          if (e?.error?.errors) {
            for (const property in e.error.errors) {
              this.openAlert(e.error.errors[property]);
            }
          } else if (e?.error?.message) {
            this.openAlert(e.error.message);
          } else {
            this.openAlert('Failed to resend OTP. Please try again.');
          }
        }
      },
    });
  }
  

  
  

  // Start Timer
  startTimer(minutes: number) {
    clearInterval(this.timerInterval);
    let seconds = minutes * 60;
  
    this.timerInterval = setInterval(() => {
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      this.resendTimer = `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`;
      seconds--;
  
      if (seconds < 0) {
        clearInterval(this.timerInterval);
        this.retryActive = true;
        this.cdr.detectChanges();
      }
    }, 1000);
  }
  

  // Open Alert Dialog
  openAlert(message: string) {
    this.dialog.open(AlertDialogComponent, {
      data: { message: message, buttonText: { cancel: "OK" } },
    });
  }

  // OTP Input Change
  onOTPChange(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    if (!/^\d*$/.test(input)) {
      this.f["otp"].setValue("");
    }
  }

  // Cleanup on destroy
  ngOnDestroy(): void {
    clearInterval(this.timerInterval);
  }
}
